import { useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FormFieldsStepRef } from "@/components/commos/FormStep";
import { useLegalsFields } from "./useLegalsFields";
import { useDetailPage } from "@/hooks/useDetailPage";
import { legalsService, type KeyNameFields } from "@/services/legalsService";

const details:{ id: KeyNameFields, label: string }[] = [
    {
        id: 'id',
        label: "ID de legal",
    },
    {
        id: 'name',
        label: "Nombre",
    },
    {
        id: 'legal_text',
        label: "Texto legal",
    },
    {
        id: 'color',
        label: "Color",
    },
];

export const useDetailLegals = () => {
    const { id } = useParams();
    const { fields } = useLegalsFields();
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const formRef = useRef<FormFieldsStepRef>(null);

    const [values, setValues] = useState<Partial<Record<KeyNameFields, any>>>({});

    const { handleDelete, onSubmit, isFetching } = useDetailPage({
        updateItem: async (legalId: string, data: any) => {
            const { id: _id, image: _image, ...updateData } = data;
            return legalsService.updateLegal(legalId, updateData);
        },
        deleteItem: legalsService.deleteLegal,
        itemName: 'legal',
        itemDisplayName: typeof values.name === 'string' ? values.name : undefined,
        tablePath: '/legals/table',
    });

    useEffect(() => {
        const fetchLegal = async () => {
            if (!id) return;
            setIsLoading(true);
            const result = await legalsService.getLegal(id);
            if (result.success && result.data) {
                setValues({
                    id: result.data.id,
                    name: result.data.name,
                    legal_text: result.data.legal_text,
                    color: result.data.color,
                });
            }
            setIsLoading(false);
        };
        void fetchLegal();
    }, [id]);

    const valueDetails = useMemo(() => {
        return details.map((detail) => ({
            ...detail,
            isHtml: detail.id === 'legal_text',
            value: values[detail.id] || '',
        }));
    }, [values]);

    return {
        valueDetails,
        isFetching,
        onSubmit,
        handleDelete,
        fields,
        isEditing,
        isLoading,
        formRef,
        values,
        setIsEditing
    }
}
